import type { CommandHandler } from '../types';
import type { ColumnMetadata } from '../../types';
import { isSummaryColumn } from '../../components/data-display/utils/columnUtils';
import { calculateClientFallback } from '../../components/data-display/utils/summaryCalculators';

/** Strips characters that are forbidden in filenames and normalises whitespace */
function sanitizePart(val: string): string {
  const forbiddenChars = /[/\\:*?"<>|]/g;
  return String(val || '')
    .trim()
    .replace(forbiddenChars, '-')
    .replace(/\s+/g, '_');
}

/** Returns columns visible for export — excludes hidden, action, system (_) and Id columns */
export function getExportColumns(columns: ColumnMetadata[]) {
  return columns.filter(
    (col) =>
      !col.hidden && col.field !== 'actions' && !col.field?.startsWith('_') && col.field !== 'Id'
  );
}

/**
 * Resolves the visible header label for a column.
 * Priority matches the grid UI: title → caption → field.
 * headerName is a legacy fallback kept for safety.
 */
export function getColumnLabel(col: ColumnMetadata): string {
  const headerName = (col as Record<string, unknown>).headerName;
  return (
    col.caption ||
    col.title ||
    (typeof headerName === 'string' ? headerName : '') ||
    col.field ||
    ''
  );
}

function getFieldValue(row: Record<string, unknown>, col: ColumnMetadata): unknown {
  const fieldName = col.dataField || col.field;
  return fieldName ? row[fieldName] : undefined;
}

type ExportRow = { kind: 'data'; row: Record<string, unknown> } | { kind: 'group'; label: string };

function getExportRows(
  rows: Record<string, unknown>[],
  columns: ColumnMetadata[],
  lookupMaps?: Map<string, Record<string, string>>,
  groupFields?: string[]
): ExportRow[] {
  // Use dynamic groupFields from context if available, fall back to column metadata
  const activeGroupFields =
    groupFields && groupFields.length > 0
      ? groupFields
      : columns
          .filter((col) => col.groupIndex != null && (col.groupIndex as number) >= 0)
          .sort((a, b) => ((a.groupIndex as number) ?? 0) - ((b.groupIndex as number) ?? 0))
          .map((col) => (col.dataField || col.field) as string)
          .filter(Boolean);

  if (activeGroupFields.length === 0) return rows.map((row) => ({ kind: 'data', row }));

  // Sort rows by group fields so group boundaries are contiguous
  const sortedRows = [...rows].sort((a, b) => {
    for (const field of activeGroupFields) {
      const av = String(a[field] ?? '');
      const bv = String(b[field] ?? '');
      if (av < bv) return -1;
      if (av > bv) return 1;
    }
    return 0;
  });

  const groupColumns = activeGroupFields
    .map((field) => columns.find((col) => (col.dataField || col.field) === field)!)
    .filter(Boolean);

  const result: ExportRow[] = [];
  let previous: string[] = [];

  for (const row of sortedRows) {
    const values = groupColumns.map((col) =>
      String(resolveCellValue(getFieldValue(row, col), col, lookupMaps))
    );
    let changedAt = values.findIndex((value, index) => value !== previous[index]);
    if (changedAt === -1) changedAt = values.length;
    for (let index = changedAt; index < values.length; index++) {
      result.push({
        kind: 'group',
        label: `${getColumnLabel(groupColumns[index])}: ${values[index]}`,
      });
    }
    result.push({ kind: 'data', row });
    previous = values;
  }
  return result;
}

function getFallbackSummary(rows: Record<string, unknown>[], col: ColumnMetadata): string {
  const type = col.totalSummaries?.toLowerCase() ?? '';
  const fieldName = col.dataField || col.field || '';

  // Wrap plain objects to match the Row<T> shape calculateClientFallback expects
  const fakeRows = rows.map((row) => ({
    original: row,
    getValue: (colId: string) => row[colId],
  })) as Parameters<typeof calculateClientFallback>[0];

  return calculateClientFallback(fakeRows, fieldName, type);
}

/**
 * Returns per-column footer values for columns that have a supported summary type.
 * serverSummary indices correspond to summary-enabled columns in summaryColumnsSource order.
 * Local filters use client totals because serverSummary represents the unfiltered dataset.
 * Returns undefined when no column has a supported summary type.
 */
export function getFooterValues(
  rows: Record<string, unknown>[],
  columns: ColumnMetadata[],
  serverSummary?: string[] | null,
  summaryColumnsSource: ColumnMetadata[] = columns,
  useServerSummary = true
): Map<ColumnMetadata, string> | undefined {
  const summaryColumns = columns.filter(isSummaryColumn);
  if (summaryColumns.length === 0) return undefined;
  const allSummaryColumns = summaryColumnsSource.filter(isSummaryColumn);

  const result = new Map<ColumnMetadata, string>();
  summaryColumns.forEach((col) => {
    const summaryIndex = allSummaryColumns.findIndex(
      (summaryColumn) =>
        (summaryColumn.dataField || summaryColumn.field) === (col.dataField || col.field)
    );
    const serverValue = summaryIndex >= 0 ? serverSummary?.[summaryIndex] : undefined;
    result.set(
      col,
      useServerSummary && serverValue != null ? serverValue : getFallbackSummary(rows, col)
    );
  });
  return result;
}

/**
 * Resolves a raw cell value into its export-ready form.
 * Applies lookup display labels, formats dates and converts booleans to Yes/No.
 */
export function resolveCellValue(
  value: unknown,
  col: ColumnMetadata,
  lookupMaps?: Map<string, Record<string, string>>
): unknown {
  const fieldName = col.dataField || col.field;

  // Replace foreign-key IDs with their human-readable display labels
  const lookupMap = fieldName ? lookupMaps?.get(fieldName) : undefined;
  if (lookupMap && value != null) {
    value = lookupMap[String(value)] || value;
  }

  // Format date/datetime columns to a locale string
  if ((col.type === 'date' || col.dataType === 'datetime') && value) {
    try {
      const date = new Date(value as string | number | Date);
      if (!isNaN(date.getTime())) {
        value = date.toLocaleDateString();
      }
    } catch {
      // Keep original value if date parsing fails
    }
  }

  // Render booleans as human-readable Yes / No
  if (col.type === 'boolean' || col.dataType === 'boolean') {
    value = value ? 'Yes' : 'No';
  }

  // Fall back to empty string for null / undefined
  return value ?? '';
}

/** Builds a timestamped filename from grid metadata and file extension (e.g. "Orders_2024-01-15.xlsx") */
export function generateFilename(
  metadata: { dataSource?: string; contentType?: string },
  extension: string
): string {
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const baseName = metadata.dataSource || metadata.contentType || 'Export';
  return `${sanitizePart(baseName)}_${timestamp}.${extension}`;
}

/** Triggers a browser file-download for an in-memory buffer */
export function downloadFile(buffer: ArrayBuffer, mimeType: string, filename: string) {
  const blob = new Blob([buffer], { type: mimeType });
  const url = window.URL.createObjectURL(blob);

  // Programmatic click on a hidden anchor to start the download
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  // Release the object URL immediately — the download is already queued
  window.URL.revokeObjectURL(url);
}

/** Handler for 'export' command — exports grid data to Excel */
export const exportCommandHandler: CommandHandler = async (_command, context) => {
  const {
    metadata,
    displayData,
    columns,
    exportData = displayData,
    exportColumns: currentColumns = columns,
    lookupMaps,
    serverSummary,
    hasActiveFilters,
    groupFields,
  } = context;

  try {
    if (!exportData || exportData.length === 0) {
      return {
        success: false,
        error: 'No data to export',
        shouldRefresh: false,
      };
    }

    // Lazy-load exceljs — bundled into its own chunk, fetched only on first Excel export
    const { default: ExcelJS } = await import('exceljs');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Export');
    const exportColumns = getExportColumns(currentColumns);

    // Define columns — sets header labels and the keys used when adding rows
    worksheet.columns = exportColumns.map((col) => ({
      header: getColumnLabel(col),
      key: col.field || col.dataField || '',
      width: 15,
    }));

    // Style the header row: bold text on a light-grey background
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    };

    // Populate data rows — each cell value is resolved through lookups / formatters
    const exportRows = getExportRows(exportData, exportColumns, lookupMaps, groupFields);
    exportRows.forEach((exportRow) => {
      if (exportRow.kind === 'group') {
        const groupRow = worksheet.addRow([exportRow.label]);
        groupRow.font = { bold: true };
        groupRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
        return;
      }
      const row = exportRow.row;
      const rowData: Record<string, unknown> = {};
      exportColumns.forEach((col) => {
        const fieldName = col.dataField || col.field;
        const colKey = col.field || col.dataField;
        if (fieldName && colKey) {
          rowData[colKey] = resolveCellValue(row[fieldName], col, lookupMaps);
        }
      });
      worksheet.addRow(rowData);
    });

    // Append totals footer row if any columns have a supported summary type
    const useServerFooter = metadata.usePaging === true || !hasActiveFilters;
    const footerMap = getFooterValues(
      exportData,
      exportColumns,
      serverSummary,
      columns,
      useServerFooter
    );
    if (footerMap) {
      const footerRow = worksheet.addRow(exportColumns.map((col) => footerMap.get(col) ?? ''));
      footerRow.font = { bold: true };
      footerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
    }

    // Auto-fit column widths based on the longest value in each column, capped at 50
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: false }, (cell) => {
        const length = cell.value ? String(cell.value).length : 10;
        maxLength = Math.max(maxLength, length);
      });
      column.width = Math.min(maxLength + 2, 50);
    });

    // Serialise workbook to buffer and trigger download
    const filename = generateFilename(metadata, 'xlsx');
    const buffer = await workbook.xlsx.writeBuffer();
    downloadFile(
      buffer,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      filename
    );

    return {
      success: true,
      message: `Exported ${exportData.length} rows to ${filename}`,
      shouldRefresh: false,
    };
  } catch (error: unknown) {
    console.error('[exportCommandHandler] Export failed:', error);
    return {
      success: false,
      error: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      shouldRefresh: false,
    };
  }
};

/** Handler for 'exporttopdf' command — exports grid data to PDF */
export const exportToPdfCommandHandler: CommandHandler = async (_command, context) => {
  const {
    metadata,
    displayData,
    columns,
    exportData = displayData,
    exportColumns: currentColumns = columns,
    lookupMaps,
    serverSummary,
    hasActiveFilters,
    groupFields,
  } = context;

  try {
    if (!exportData || exportData.length === 0) {
      return {
        success: false,
        error: 'No data to export',
        shouldRefresh: false,
      };
    }

    // Lazy-load jspdf + autotable in parallel — separate chunks, fetched only on first PDF export
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);

    // Landscape A4 gives more horizontal room for wide tables
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    // Title block at the top of the page — grid name and generation timestamp
    const title = metadata.dataSource || metadata.contentType || 'Export';
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    const exportColumns = getExportColumns(currentColumns);
    const headers = exportColumns.map(getColumnLabel);

    const body: string[][] = [];
    for (const exportRow of getExportRows(exportData, exportColumns, lookupMaps, groupFields)) {
      if (exportRow.kind === 'group') {
        // Group header row: label in first cell, rest empty — detected below by shape, not content
        body.push([exportRow.label, ...exportColumns.slice(1).map(() => '')]);
      } else {
        body.push(
          exportColumns.map((col) =>
            String(resolveCellValue(getFieldValue(exportRow.row, col), col, lookupMaps))
          )
        );
      }
    }

    // Append totals footer row if any columns have a supported summary type
    const useServerFooter = metadata.usePaging === true || !hasActiveFilters;
    const footerMap = getFooterValues(
      exportData,
      exportColumns,
      serverSummary,
      columns,
      useServerFooter
    );
    if (footerMap) {
      body.push(exportColumns.map((col) => footerMap.get(col) ?? ''));
    }

    // Index of the footer row inside body (used for styling in didParseCell)
    const footerBodyIndex = footerMap ? body.length - 1 : -1;

    // Render table starting below the title block (startY: 28 mm)
    autoTable(doc, {
      head: [headers],
      body,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      didParseCell: (data) => {
        if (data.section !== 'body') return;

        // Detect group header rows by shape: only the first cell is non-empty.
        // Avoids false positives from data values that happen to contain ': '.
        if (data.row.index !== footerBodyIndex) {
          const rawRow = data.row.raw as string[];
          const isGroupRow =
            rawRow.length > 1 &&
            typeof rawRow[0] === 'string' &&
            rawRow[0].length > 0 &&
            rawRow.slice(1).every((cell) => cell === '');
          if (isGroupRow) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [242, 242, 242];
          }
        }

        // Style the totals footer row
        if (data.row.index === footerBodyIndex) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 232, 232];
        }
      },
      margin: { top: 28 },
    });

    // Serialise and trigger download via jsPDF's built-in save
    const filename = generateFilename(metadata, 'pdf');
    doc.save(filename);

    return {
      success: true,
      message: `Exported ${exportData.length} rows to ${filename}`,
      shouldRefresh: false,
    };
  } catch (error: unknown) {
    console.error('[exportToPdfCommandHandler] Export failed:', error);
    return {
      success: false,
      error: `PDF export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      shouldRefresh: false,
    };
  }
};
